// AdminWsListener.jsx
import { useEffect, useRef } from "react";
import { notification } from "antd";
import { API_BASE_URL, WS_ENDPOINT_URL } from "configs/AppConfig";
import { ensureWS, subscribe } from "./wsClient";
import { useAdminNotifications } from "contexts/AdminNotificationsContext";
import { playNotificationSound } from "utils/notificationSound";

export default function AdminWsListener() {
  const { addFromWs } = useAdminNotifications();
  const tokenPollRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }

    function getToken() {
      const raw = localStorage.getItem("auth_token");
      if (!raw) return null;
      return raw.replace(/^Bearer\s+/i, "").replace(/^"|"$/g, "");
    }

    function getWsUrl() {
      const base = (WS_ENDPOINT_URL || API_BASE_URL || "").replace(/\/api\/?$/, "");
      return WS_ENDPOINT_URL
        ? WS_ENDPOINT_URL
        : base.replace(/^http/, "ws") + "/ws/";
    }

    const NOTIF_EVENT_CONFIG = {
      "admin.notification": { sound: true, toast: true, browser: true },
      "booking.created":    { sound: true, toast: true, browser: true },
      "booking.updated":    { sound: true, toast: false, browser: true },
      "patient.checkin.created": { sound: true, toast: true, browser: true },
      "patient.intake.created":  { sound: true, toast: true, browser: true },
      "lead.created":       { sound: true, toast: true, browser: true },
      "demo.requested":     { sound: true, toast: true, browser: true },
      "client.created":     { sound: true, toast: true, browser: true },
      "invoice.created":    { sound: true, toast: false, browser: false },
      "invoice.paid":       { sound: true, toast: true, browser: true },
      "payment.received":   { sound: true, toast: true, browser: true },
      "estimate.created":   { sound: true, toast: false, browser: false },
      "estimate.accepted":  { sound: true, toast: true, browser: true },
      "estimate.rejected":  { sound: true, toast: true, browser: true },
      "contract.created":   { sound: true, toast: false, browser: false },
      "contract.signed":    { sound: true, toast: true, browser: true },
      "contract.declined":  { sound: true, toast: true, browser: true },
      "reminder.due":       { sound: true, toast: true, browser: true },
      "onboarding.pack_sent": { sound: true, toast: true, browser: true },
      "onboarding.completed": { sound: true, toast: true, browser: true },
      "voice.device_online": { sound: true, toast: true, browser: true },
      "voice.recording_ready": { sound: true, toast: true, browser: true },
    };

    function handleMessage(ev) {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }

      if (msg?.type === "pong") return;

      const { event, payload } = msg || {};
      if (!event || !payload) return;

      if (event === 'chat:message') {
        window.dispatchEvent(new CustomEvent('codeforce:chat-message', { detail: payload }));

        // Field marketers are handled by MarketerWsListener (avoid duplicate toasts)
        let isMarketer = false;
        try {
          const authUser = JSON.parse(localStorage.getItem('auth_user') || 'null');
          isMarketer = Boolean(authUser?.marketerOnly || authUser?.role === 'marketer');
        } catch { /* ignore */ }
        if (isMarketer) return;

        const sender =
          payload.message?.sender?.name ||
          payload.message?.sender?.username ||
          'Someone';
        const preview = (payload.message?.content || 'New chat message').substring(0, 120);
        const onChatPage = /\/codeforce\/chat\/?$/.test(window.location.pathname);

        try {
          playNotificationSound();
        } catch { /* ignore */ }

        // In-app toast when admin is on another page
        if (!onChatPage) {
          notification.open({
            message: 'Chat received',
            description: `${sender}: ${preview}`,
            duration: 6,
            placement: 'topRight',
            onClick: () => {
              window.location.assign('/app/codeforce/chat');
            },
          });
        }

        // Browser notification when tab/app is in background
        if (
          typeof Notification !== 'undefined' &&
          Notification.permission === 'granted' &&
          document.visibilityState === 'hidden'
        ) {
          const n = new Notification('Chat received', {
            body: `${sender}: ${preview}`,
            icon: '/logo192.png',
            tag: `chat-${payload.roomId || 'room'}`,
            data: { url: '/app/codeforce/chat' },
          });
          n.onclick = () => {
            window.focus();
            window.location.assign('/app/codeforce/chat');
            n.close();
          };
        }
        return;
      }

      if (event === 'location.update') {
        window.dispatchEvent(new CustomEvent('codeforce:location-update', { detail: payload }));
        return;
      }

      if (event === 'voice.device') {
        window.dispatchEvent(new CustomEvent('codeforce:voice-device', { detail: payload }));
        return;
      }

      // Prefer nested business event (e.g. payment.received) when WS wraps as admin.notification
      const businessEvent = payload.event || event;
      const cfg = NOTIF_EVENT_CONFIG[businessEvent] || NOTIF_EVENT_CONFIG[event];
      if (!cfg) return;

      if (cfg.sound) playNotificationSound();

      addFromWs({ ...payload, event: businessEvent });

      if (cfg.browser && Notification?.permission === "granted") {
        new Notification(payload.title || "CodeForce BMS", {
          body: payload.body || "",
        });
      }

      if (cfg.toast) {
        notification.info({
          message: payload.title || "New Notification",
          description: payload.body || "",
          duration: 5,
          placement: "topRight",
        });
      }
    }

    function start() {
      const token = getToken();
      if (!token) return false;

      const wsUrl = getWsUrl();
      console.log("[ws] starting singleton on", wsUrl);

      ensureWS(wsUrl, token);

      const unsub = subscribe(handleMessage);
      return unsub;
    }

    // Try start immediately
    let cleanup = start();
    cleanupRef.current = cleanup || null;

    // If token not ready yet, poll for it for a bit
    if (!cleanup) {
      tokenPollRef.current = setInterval(() => {
        if (cleanupRef.current) return;

        const maybeCleanup = start();
        if (maybeCleanup) {
          cleanupRef.current = maybeCleanup;
          clearInterval(tokenPollRef.current);
          tokenPollRef.current = null;
        }
      }, 1000);
    }

    return () => {
      if (tokenPollRef.current) {
        clearInterval(tokenPollRef.current);
        tokenPollRef.current = null;
      }
      if (typeof cleanupRef.current === "function") {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [addFromWs]);

  return null;
}
